import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCountrySource } from 'legacy/lut/hooks/useCountrySource'
import { useCountryStateSource } from 'legacy/lut/hooks/useCountryStateSource'
import { memo, useCallback, useEffect } from 'react'

import { PartnerFields } from 'clickNgo/entities/partner/configs'
import { usePartnerInfoFormData } from 'clickNgo/entities/partner/libs/hooks/form'
import { RenderWhen } from 'cng-common/components/renderWhen'
import {
  FormDropdown,
  FormInput,
  updateFormDataField,
  useGetFormDataField,
} from 'cng-common/features/form'
import { FieldWrapperWithChanges, ReadOnlyField } from 'cng-common/features/form/components'
import { wasFieldModified } from 'cng-common/features/form/helpers'
import { Translation } from 'cng-common/features/translations'
import { Checkbox } from 'core-components/library/checkbox'
import { FieldGroup } from 'shared/components/fieldGroup'

import { partnerLegalInfoTranslation } from '../../translation'

import { fieldsForCopy } from './configs'
import { ILegalAddressBlockProps } from './models'

import styles from './LegalAddressBlock.module.scss'

export const LegalAddressBlock = memo<ILegalAddressBlockProps>(
  ({
    formKeys,
    legalAddressLine1,
    legalAddressLine2,
    legalAddressLine3,
    legalCountry,
    legalModifiedValues,
    legalState,
    legalCity,
    legalZipCode,
    isDisabled,
    isDisabledByRegion,
    isReadOnly,
    isNewPartner,
    isLegalAddressLine1Required,
    isLegalAddressLine2Required,
    isLegalAddressLine3Required,
    isLegalCityRequired,
    isLegalCountryRequired,
    isLegalStateRequired,
    isLegalZipCodeRequired,
    onLegalAddressLine1Validate,
    onLegalAddressLine2Validate,
    onLegalAddressLine3Validate,
    onLegalCityValidate,
    onLegalCountryValidate,
    onLegalStateValidate,
    onLegalZipCodeValidate,
  }) => {
    /** -------------------- Variables ----------------------- */

    const dispatch = useAppDispatch()
    const [doCopyBasicAddress, setDoCopyBasicAddress] = useGetFormDataField<boolean>(
      formKeys.settings,
      PartnerFields.doCopyBasicAddress.field
    )
    const { updateLegalInfoFormData, basicInfoFormData } = usePartnerInfoFormData({
      formKeys,
    })
    const legalCountrySource = useCountrySource({ isStatic: true })
    const [legalCountryFormField] = useGetFormDataField<string>(
      formKeys.legal,
      PartnerFields.legalCountry.field
    )
    const legalStateSource = useCountryStateSource({
      countryName: legalCountryFormField || legalCountry,
      state: legalState,
      isStatic: true,
    })

    const isCountryFieldDisabled = isDisabled || isDisabledByRegion || !isNewPartner
    const isStateFieldDisabled =
      (!legalCountryFormField && !legalCountry) || isDisabled || isDisabledByRegion

    // This hack is needed to prevent resetting the state value in the field when changing the country
    const isLegalStateAllowCreate = isStateFieldDisabled || doCopyBasicAddress
    const isCopyBasicAddressChecked = Boolean(doCopyBasicAddress)

    /** -------------------- Handlers ----------------------- */

    const handleChangeLegalCountry = useCallback(() => {
      dispatch(
        updateFormDataField({
          formKey: formKeys.legal,
          field: PartnerFields.legalState.field,
          value: '',
        })
      )
    }, [dispatch, formKeys.legal])

    useEffect(() => {
      if (doCopyBasicAddress) {
        fieldsForCopy.forEach(({ legal, basic }) => {
          const value = doCopyBasicAddress ? basicInfoFormData[basic] : ''
          const doRevalidate = true
          updateLegalInfoFormData({ field: legal, value, doRevalidate })
        })
      }
    }, [basicInfoFormData, doCopyBasicAddress, updateLegalInfoFormData])

    return (
      <FieldGroup title={partnerLegalInfoTranslation.content.blockTitles.legalAddressBlock}>
        <div className={styles.fullWidth}>
          <RenderWhen condition={isReadOnly || isDisabled}>
            <ReadOnlyField
              value={isCopyBasicAddressChecked}
              label={PartnerFields.doCopyBasicAddress.label}
            />
          </RenderWhen>
          <RenderWhen condition={!isReadOnly && !isDisabled}>
            <Checkbox
              disabled={isDisabled}
              className={styles.checkbox}
              checked={isCopyBasicAddressChecked}
              onChange={setDoCopyBasicAddress}
              label={<Translation text={PartnerFields.doCopyBasicAddress.label} span />}
            />
          </RenderWhen>
        </div>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            legalModifiedValues,
            PartnerFields.legalAddressLine1.externalField
          )}
        >
          <FormInput
            formKey={formKeys.legal}
            field={PartnerFields.legalAddressLine1.field}
            label={PartnerFields.legalAddressLine1.label}
            defaultValue={legalAddressLine1}
            isDisabled={isDisabled || doCopyBasicAddress}
            isReadOnly={isReadOnly}
            isRequired={isLegalAddressLine1Required}
            onValidate={onLegalAddressLine1Validate}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            legalModifiedValues,
            PartnerFields.legalAddressLine2.externalField
          )}
        >
          <FormInput
            formKey={formKeys.legal}
            field={PartnerFields.legalAddressLine2.field}
            label={PartnerFields.legalAddressLine2.label}
            defaultValue={legalAddressLine2}
            isDisabled={isDisabled || doCopyBasicAddress}
            isReadOnly={isReadOnly}
            isRequired={isLegalAddressLine2Required}
            onValidate={onLegalAddressLine2Validate}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            legalModifiedValues,
            PartnerFields.legalAddressLine3.externalField
          )}
        >
          <FormInput
            formKey={formKeys.legal}
            field={PartnerFields.legalAddressLine3.field}
            label={PartnerFields.legalAddressLine3.label}
            defaultValue={legalAddressLine3}
            isDisabled={isDisabled || doCopyBasicAddress}
            isReadOnly={isReadOnly}
            isRequired={isLegalAddressLine3Required}
            onValidate={onLegalAddressLine3Validate}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            legalModifiedValues,
            PartnerFields.legalCountry.externalField
          )}
        >
          <FormDropdown
            formKey={formKeys.legal}
            field={PartnerFields.legalCountry.field}
            label={PartnerFields.legalCountry.label}
            source={legalCountrySource}
            caption="name"
            defaultValue={legalCountry}
            onChange={handleChangeLegalCountry}
            onValidate={onLegalCountryValidate}
            isDisabled={isCountryFieldDisabled || doCopyBasicAddress}
            isReadOnly={isReadOnly}
            isRequired={isLegalCountryRequired}
            isStatic
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(legalModifiedValues, PartnerFields.legalState.externalField)}
        >
          <FormDropdown
            formKey={formKeys.legal}
            field={PartnerFields.legalState.field}
            label={PartnerFields.legalState.label}
            source={legalStateSource}
            caption="key"
            defaultValue={legalState}
            onValidate={onLegalStateValidate}
            isDisabled={isStateFieldDisabled || doCopyBasicAddress}
            allowCreate={isLegalStateAllowCreate}
            isRequired={isLegalStateRequired}
            isReadOnly={isReadOnly}
            isStatic
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(legalModifiedValues, PartnerFields.legalCity.externalField)}
        >
          <FormInput
            formKey={formKeys.legal}
            field={PartnerFields.legalCity.field}
            label={PartnerFields.legalCity.label}
            defaultValue={legalCity}
            onValidate={onLegalCityValidate}
            isDisabled={isDisabled || doCopyBasicAddress}
            isReadOnly={isReadOnly}
            isRequired={isLegalCityRequired}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            legalModifiedValues,
            PartnerFields.legalZipCode.externalField
          )}
        >
          <FormInput
            formKey={formKeys.legal}
            field={PartnerFields.legalZipCode.field}
            label={PartnerFields.legalZipCode.label}
            defaultValue={legalZipCode}
            onValidate={onLegalZipCodeValidate}
            isDisabled={isDisabled || doCopyBasicAddress}
            isReadOnly={isReadOnly}
            isRequired={isLegalZipCodeRequired}
          />
        </FieldWrapperWithChanges>
      </FieldGroup>
    )
  }
)
